import { useState, useCallback, useRef } from 'react';
import type { InsuredResult } from './InsuredMentionDropdown';

const SEARCH_API = `${process.env.REACT_APP_MyAMP_Url}/api/ams/search-insureds`;

// ── XML entity tag builder ─────────────────────────────────────────────────────
// Converts a selected InsuredResult into a structured XML reference tag
// so AI backends can parse the entity type, name, and ID unambiguously.
//
// Examples:
//   Insured  → <insured><name>Pravin_Commercialtest</name><id>b06f8712-...</id></insured>
//   Prospect → <prospect><name>contact ohio, Test</name><id>xxx</id></prospect>
//   Policy   → <policy><number>MTM_4328_PCA0000034606</number><id>xxx</id></policy>

function escapeXml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

export function buildEntityXmlTag(entity: InsuredResult): string {
  const type = (entity.typeName || '').toLowerCase();
  const id   = escapeXml(entity.id || '');
  const name = escapeXml(entity.name || '');

  if (type === 'policy') {
    const num = escapeXml(entity.policyNumber || entity.name || '');
    return `<policy><number>${num}</number><id>${id}</id></policy>`;
  }
  if (type === 'prospect') {
    return `<prospect><name>${name}</name><id>${id}</id></prospect>`;
  }
  if (type === 'carrier') {
    return `<carrier><name>${name}</name><id>${id}</id></carrier>`;
  }
  if (type === 'principal') {
    return `<principal><name>${name}</name><id>${id}</id></principal>`;
  }
  // Default: insured
  return `<insured><name>${name}</name><id>${id}</id></insured>`;
}

// Matches an active @ mention at the END of the input value:
// e.g. "hello @jo" → active, query = "jo"
//      "hello @we are" → active, query = "we are"  (spaces allowed)
//      "@" → active, query = ""
const MENTION_RE = /(?:^|\s)@(.*)$/;

interface MentionState {
  active: boolean;
  query: string;
  triggerPos: number; // index of '@' in the value
}

const IDLE: MentionState = { active: false, query: '', triggerPos: -1 };

export function useInsuredMention() {
  const [mention, setMention] = useState<MentionState>(IDLE);
  const [results, setResults] = useState<InsuredResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [insertedNames, setInsertedNames] = useState<string[]>([]);
  const [insertedEntities, setInsertedEntities] = useState<Map<string, InsuredResult>>(new Map());
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const search = useCallback(async (query: string) => {
    if (query.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const authToken = localStorage.getItem('authToken') || '';
      const amsToken = localStorage.getItem('amsToken') || '';
      const res = await fetch(SEARCH_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ searchText: query, nc_token: amsToken }),
      });
      if (!res.ok) throw new Error('Search failed');
      const data = await res.json();
      setResults(Array.isArray(data) ? data : (data.insureds || []));
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /** Call on every input change. Returns the same value unchanged. */
  const onValueChange = useCallback(
    (val: string) => {
      // Keep only names still present in the value
      setInsertedNames(prev => prev.filter(n => val.includes(n)));
      setInsertedEntities(prev => {
        const next = new Map(prev);
        Array.from(next.keys()).forEach(name => {
          if (!val.includes(name)) next.delete(name);
        });
        return next;
      });

      const match = MENTION_RE.exec(val);
      if (match) {
        const query = match[1]; // text after @
        const atPos = val.lastIndexOf('@');
        setMention({ active: true, query, triggerPos: atPos });
        setActiveIdx(0);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => search(query), 280);
      } else {
        close();
      }
    },
    [search]
  );

  const close = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setMention(IDLE);
    setResults([]);
    setLoading(false);
    setActiveIdx(0);
  }, []);

  const clearInsertedNames = useCallback(() => {
    setInsertedNames([]);
    setInsertedEntities(new Map());
  }, []);

  /** Returns the new input value after selecting an insured */
  const select = useCallback(
    (ins: InsuredResult, currentValue: string): string => {
       const name = ins.typeName === 'Policy'
        ? (ins.policyNumber || ins.name || 'Unknown')
        : (ins.name || 'Unknown');
      const before = currentValue.slice(0, mention.triggerPos);
      const after = currentValue.slice(mention.triggerPos).replace(/@.*$/, name);
      close();
      setInsertedNames(prev => [...prev, name]);
      setInsertedEntities(prev => new Map(prev).set(name, ins));
      return before + after;
    },
    [mention.triggerPos, close]
  );

  /** Handle keyboard navigation. Returns true if the key was consumed. */
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent, currentValue: string, onSelect: (val: string) => void): boolean => {
      if (!mention.active) return false;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx(i => Math.min(i + 1, results.length - 1));
        return true;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx(i => Math.max(i - 1, 0));
        return true;
      }
      if (e.key === 'Enter' && results.length > 0) {
        e.preventDefault();
        onSelect(select(results[activeIdx], currentValue));
        return true;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return true;
      }
      return false;
    },
    [mention.active, results, activeIdx, select, close]
  );

  return {
    mentionActive: mention.active,
    mentionQuery: mention.query,
    mentionResults: results,
    mentionLoading: loading,
    mentionActiveIdx: activeIdx,
    setMentionActiveIdx: setActiveIdx,
    insertedNames,
    insertedEntities,
    clearInsertedNames,
    onValueChange,
    onKeyDown,
    selectMention: select,
    closeMention: close,
  };
}
